// About — short manifesto + stat strip + ways in.
import { Link } from 'react-router-dom';
import { CRWIcon, type IconName } from '../components/icons';
import { MetalBtn } from '../components/primitives';
import { DISCORD_INVITE, SUPPORT_EMAIL } from '../sections/footer';
import { MetaChip, PageShell } from './shell';

const STATS: [string, string][] = [
  ['100%', 'local — footage never leaves your machine'],
  ['110+', 'spoken languages searchable'],
  ['1', 'click from search result to timeline'],
  ['2026', 'founded · Bulgaria'],
];

export function AboutPage() {
  return (
    <PageShell
      kicker="About"
      title={<>Made by an editor who got tired of scrubbing.</>}
      sub="ClipRadar exists because 'I know we shot it, give me twenty minutes' is not an answer professional editors should still be giving in 2026."
      meta={<><MetaChip>NDX Group · Bulgaria</MetaChip><MetaChip>In beta</MetaChip></>}
    >
      <div className="crw-prose" style={{ maxWidth: 640 }}>
        <p>
          Every editor knows the feeling: the shot exists, somewhere, across three drives and nine hundred clips —
          and the only search engine available is your own memory plus a scrub bar. ClipRadar is the radar we
          wished we had: type what you remember — what was on screen, what was said, who was in frame — and land
          on the exact moment.
        </p>
        <p>
          One decision shaped everything else: <strong>everything runs locally</strong>. Indexing, transcription,
          face recognition, search — all of it happens on your machine, which means your unreleased footage,
          client material, and interview subjects never touch anyone's cloud, including ours. It also means
          search results come back in milliseconds, not round-trips.
        </p>
        <p>
          ClipRadar is built by <strong>NDX Group</strong>, a small independent company incorporated in Bulgaria,
          and it's currently in <strong>beta</strong> — which means the roadmap is genuinely up for grabs. The
          features we ship next are the ones beta users vote loudest for.
        </p>
      </div>

      <div className="crw-about-stats" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, margin: '36px 0' }}>
        {STATS.map(([big, small]) => (
          <div key={big} style={{ padding: '18px 16px', borderRadius: 12, border: '1px solid var(--line-1)', background: 'var(--surface-card)' }}>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: 30, fontWeight: 700, letterSpacing: 'var(--tracking-display)', color: 'var(--fg-1)' }}>{big}</div>
            <div style={{ marginTop: 4, fontSize: 12.5, lineHeight: 1.5, color: 'var(--fg-3)' }}>{small}</div>
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 14, padding: '26px 24px', borderRadius: 14, border: '1px solid var(--line-2)', background: 'var(--surface-card)' }}>
        <span className="cr-label" style={{ color: 'var(--signal-400)' }}>Join the build</span>
        <p style={{ margin: 0, fontSize: 14, lineHeight: 1.65, color: 'var(--fg-2)', maxWidth: 480 }}>
          ClipRadar is in open beta — try it free on your own footage (40 searches, no card), and lock in
          founding pricing at $199 one-time before it goes to $349. The{' '}
          <a href={DISCORD_INVITE} target="_blank" rel="noopener noreferrer" className="crw-textlink" style={{ color: 'var(--signal-400)' }}>Discord</a>{' '}
          is where the roadmap gets argued about. Or just write to{' '}
          <a href={`mailto:${SUPPORT_EMAIL}`} className="crw-textlink" style={{ color: 'var(--signal-400)' }}>{SUPPORT_EMAIL}</a> — it lands
          straight in the founder's inbox.
        </p>
        <Link to="/trial" style={{ textDecoration: 'none', display: 'flex' }}>
          <MetalBtn style={{ fontFamily: 'var(--font-mono)', letterSpacing: '0.08em', textTransform: 'uppercase' }}>Start Free Trial</MetalBtn>
        </Link>
      </div>
    </PageShell>
  );
}

export function ContactPage() {
  const cards: { icon: IconName; title: string; body: string; cta: string; href: string }[] = [
    {
      icon: 'at', title: 'Email',
      body: 'Anything at all — support, licenses, refunds, press, partnership, or just to say the search nailed a shot.',
      cta: SUPPORT_EMAIL, href: `mailto:${SUPPORT_EMAIL}`,
    },
    {
      icon: 'message', title: 'Discord',
      body: 'The fastest answers, straight from the team and other editors. Support, feature votes, and beta news live here.',
      cta: 'Join the server', href: DISCORD_INVITE,
    },
    {
      icon: 'zap', title: 'Bugs & feature requests',
      body: 'Found something broken, or something missing? The 🐛 bugs-and-issues and 💡 feature-requests forums on Discord are where they get tracked — and upvoted.',
      cta: 'Open the forums', href: DISCORD_INVITE,
    },
  ];
  return (
    <PageShell
      kicker="Contact"
      title="Talk to a human."
      sub="No ticket systems, no chatbots-pretending-to-care. A small team that reads everything, usually within a day."
      wide
    >
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 14 }}>
        {cards.map((c) => (
          <a key={c.title} href={c.href} target={c.href.startsWith('mailto') ? undefined : '_blank'} rel="noopener noreferrer"
            className="crw-contact-card"
            style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '24px 22px', borderRadius: 14, border: '1px solid var(--line-2)', background: 'var(--surface-card)', textDecoration: 'none', transition: 'border-color 160ms var(--ease-out), background 160ms var(--ease-out), transform 160ms var(--ease-out)' }}>
            <span style={{ display: 'grid', placeItems: 'center', width: 38, height: 38, borderRadius: 11, background: 'var(--signal-dim)', border: '1px solid rgba(74,227,255,0.25)', color: 'var(--signal-400)' }}>
              <CRWIcon d={c.icon} size={17} />
            </span>
            <span style={{ fontFamily: 'var(--font-display)', fontSize: 17, fontWeight: 600, letterSpacing: 'var(--tracking-heading)', color: 'var(--fg-1)' }}>{c.title}</span>
            <span style={{ fontSize: 13.5, lineHeight: 1.65, color: 'var(--fg-2)', flex: 1 }}>{c.body}</span>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, letterSpacing: '0.04em', color: 'var(--signal-400)' }}>{c.cta} →</span>
          </a>
        ))}
      </div>
      <p style={{ margin: '26px 0 0', fontFamily: 'var(--font-mono)', fontSize: 11, letterSpacing: '0.06em', color: 'var(--fg-4)', textTransform: 'uppercase' }}>
        NDX Group · Bulgaria · replies usually within 24–48h
      </p>
    </PageShell>
  );
}
